import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { faFolder } from '@fortawesome/free-solid-svg-icons/faFolder';
import { faDesktop } from '@fortawesome/free-solid-svg-icons/faDesktop';
import { faCrosshairs } from '@fortawesome/free-solid-svg-icons/faCrosshairs';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import { faThLarge } from '@fortawesome/free-solid-svg-icons/faThLarge';
import { Sidebar } from '@corrprocanada/theme-kit/web';
import {
	getCompanyProducts as getCompanyProductsAction
} from '../../redux/actions';
import { canSwitchCompany, getActiveCompany } from '../../../util/user/companySwitch';
import AccountUtil from '../../../account/components/utils/AccountUtil';
import ConfirmDialog from '../shared/ConfirmDialog';
import { fetchCompanyDirectory } from '../CompanySwitch/utils/companyDirectory';
import { NO_COMPANY_PRODUCTS } from './utils/companyProducts';
import releaseVersion from '../layout/ReleaseVersion';

import './LeftPanel.scss';

const mapStateToProps = state => {
	const {
		main: { companyProducts }
	} = state;
	return { companyProducts };
};
const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			getCompanyProducts: getCompanyProductsAction
		},
		dispatch
	);

// ── LeftPanel (main component) ──────────────────────────────
const LeftPanel = ({
	getCompanyProducts,
	companyProducts,
	location,
	history
}) => {
	const [collapsed, setCollapsed] = useState(() => {
		try {
			const saved = localStorage.getItem('cg_sidebar_collapsed');
			return saved !== null ? saved === 'true' : true;
		} catch {
			return true;
		}
	});
	const [switchableCompanyCount, setSwitchableCompanyCount] = useState(null);
	const [isSignOutConfirmOpen, setIsSignOutConfirmOpen] = useState(false);
	// Which products the company being viewed actually has data for.  Held in the
	// store so the sidebar and the router agree about what is reachable; null
	// while the probe is still in flight.
	const products = companyProducts || NO_COMPANY_PRODUCTS;

	// Operators (AIP admins) can act on behalf of a single customer company.
	const canSwitch = useMemo(() => canSwitchCompany(), []);
	const activeCompany = useMemo(() => getActiveCompany(), []);
	const showCompanySwitch = canSwitch && switchableCompanyCount > 1;

	useEffect(() => {
		if (!canSwitch) {
			setSwitchableCompanyCount(0);
			return undefined;
		}
		let cancelled = false;
		// Same merged catalogue the picker itself uses, so the nav item appears
		// whenever the picker would actually offer a choice.
		fetchCompanyDirectory({
			onCached: list => {
				if (!cancelled) { setSwitchableCompanyCount(list.length); }
			}
		})
			.then(list => {
				if (!cancelled) { setSwitchableCompanyCount(list.length); }
			})
			.catch(() => {
				if (!cancelled) { setSwitchableCompanyCount(0); }
			});
		return () => {
			cancelled = true;
		};
	}, [canSwitch]);

	// Probe the viewed company for data if the router has not already done so.
	// Answers are cached per company, so this is a no-op on later mounts.
	useEffect(() => {
		if (!companyProducts) {
			getCompanyProducts();
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Sync collapse state with main body class + localStorage
	useEffect(() => {
		const mainBody = document.querySelector('.main-body');
		if (mainBody) {
			if (collapsed) {
				mainBody.classList.add('main-body-minify');
			} else {
				mainBody.classList.remove('main-body-minify');
			}
		}
		try {
			localStorage.setItem('cg_sidebar_collapsed', String(collapsed));
		} catch {
			// ignore
		}
	}, [collapsed]);

	const pathname = useMemo(
		() => (location ? location.pathname : window.location.pathname),
		[location]
	);

	const search = useMemo(
		() => (location ? location.search : window.location.search),
		[location]
	);

	// An application is offered when the company being viewed has data for it.
	const showCorrMonitor = products.hasCp || products.hasXli;
	const showTracking = products.hasTracking;

	// Active state helpers
	const isActiveExact = useCallback(path => pathname === path, [pathname]);
	const isActivePrefix = useCallback(path => pathname.startsWith(path), [pathname]);

	// CorrLogix tab detection (via ?tab= query param)
	const activeTab = useMemo(() => {
		const params = new URLSearchParams(search);
		return params.get('tab') || 'all-projects';
	}, [search]);

	// All Projects is now its own route; fallback: old CorrLogix default tab
	const isAllProjects =
		isActivePrefix('/all-projects') ||
		(isActiveExact('/CorrLogix') && activeTab === 'all-projects');
	const isMyProjects =
		isActivePrefix('/my-projects') ||
		(isActivePrefix('/CorrLogix') && activeTab === 'my-projects');

	const handleToggle = useCallback(open => {
		setCollapsed(!open);
	}, []);

	const openSignOutConfirm = useCallback(() => {
		setIsSignOutConfirmOpen(true);
	}, []);

	const closeSignOutConfirm = useCallback(() => {
		setIsSignOutConfirmOpen(false);
	}, []);

	const handleConfirmSignOut = useCallback(() => {
		AccountUtil.logoff().then(() => {
			window.location = '/Account/Login';
		});
	}, []);

	const handleSwitchCompanyClick = useCallback(() => {
		history.push('/switch-company');
	}, [history]);

	// actie sidebar item
	const activeNavId = useMemo(() => {
		if (isAllProjects) { return 'all-projects'; }
		if (isMyProjects) { return 'my-projects'; }
		if (isActivePrefix('/CorrMonitor') || isActivePrefix('/AssetView')) { return 'corrmonitor'; }
		if (isActivePrefix('/Tracking')) { return 'tracking'; }
		if (isActivePrefix('/my-account')) { return 'my-account'; }
		if (isActivePrefix('/help-support')) { return 'help-support'; }
		if (isActivePrefix('/switch-company')) { return 'switch-company'; }
		return '';
	}, [isAllProjects, isMyProjects, isActivePrefix]);

	const groups = useMemo(() => {
		const result = [
			{
				label: 'Projects',
				items: [
					{ id: 'all-projects', label: 'All Projects', icon: faThLarge, href: '/all-projects' },
					{ id: 'my-projects', label: 'My Projects', icon: faFolder, href: '/my-projects' }
				]
			}
		];

		if (showCorrMonitor || showTracking) {
			result.push({
				label: 'Applications',
				items: [
					...(showCorrMonitor
						? [{ id: 'corrmonitor', label: 'CorrMonitor', icon: faDesktop, href: '/CorrMonitor' }]
						: []),
					...(showTracking
						? [{ id: 'tracking', label: 'Tracking', icon: faCrosshairs, href: '/Tracking' }]
						: [])
				]
			});
		}

		result.push({
			label: 'Account',
			items: [
				{ id: 'my-account', label: 'My Account', icon: faUser, href: '/my-account' },
				{ id: 'help-support', label: 'Help & Support', icon: faQuestionCircle, href: '/help-support' }
			]
		});

		return result;
	}, [showCorrMonitor, showTracking]);

	const handleSelectNavItem = useCallback(
		(id, href) => {
			if (href) {
				history.push(href);
			}
		},
		[history]
	);

	return (
		<div className="cg-sidebar-shell">
			<Sidebar
				groups={groups}
				activeNavId={activeNavId}
				onSelectNavItem={handleSelectNavItem}
				titlePart1="CORR"
				titlePart2="GUARD"
				appName={'PORTAL'}
				startOpen={!collapsed}
				onToggle={handleToggle}
				subheaderButtonTitle="Viewing Company"
				subheaderButtonText={activeCompany}
				onSubHeaderButtonClick={handleSwitchCompanyClick}
				subHeaderButtonDisabled={!showCompanySwitch}
				onSignOut={openSignOutConfirm}
				showSignOutButton={true}
				footerText={releaseVersion}
			/>
			<ConfirmDialog
				cancelLabel="Cancel"
				confirmLabel="Sign Out"
				confirmVariant="danger"
				description="You will be returned to the login screen."
				isOpen={isSignOutConfirmOpen}
				onCancel={closeSignOutConfirm}
				onConfirm={handleConfirmSignOut}
				title="Sign out?"
			/>
		</div>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(LeftPanel));
